// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Path safety for BOTH zip-entry names and sourcemap `sources[]`.
 *
 * `sources[]` IS ATTACKER-SHAPED IN THE ORDINARY CASE, not just the hostile one. Measured on a real
 * Vite map: `sources: ["../../src/live.js", "../../src/main.js"]` — literally traversal-shaped from
 * an ordinary build. Real-world shapes also include `webpack://app/./src/x.js`,
 * `webpack://_N_E/./pages/index.tsx`, absolute `/Users/<you>/…`, `C:\Users\…`, `file:///…`,
 * `http://evil.example/x.js`, `null`, and embedded NUL bytes.
 *
 * leakmap never writes to disk — but it writes `recovered-source.zip`, and THE OS UNZIPPER HONOURS
 * `../` IN AN ENTRY NAME. fflate sanitises nothing: `../../../etc/passwd`, `/abs/root.txt`,
 * `..\win\evil.txt`, `bell\u0007.txt`, `con.txt` and `trailingdot.` all round-trip VERBATIM through
 * `zipSync`/`unzipSync`. So the boundary has to be here.
 *
 * THE RULE HAS TO SEPARATE TWO CASES THAT LOOK IDENTICAL, and an earlier version of this file did
 * not — it rejected every `..`, which is "safe" and also meant SOURCE RECOVERY SILENTLY PRODUCED
 * NOTHING ON AN ORDINARY VITE BUILD. That bug was invisible to the unit tests, because those tests
 * had encoded the same wrong rule; it was caught by running the real pipeline over a real build.
 *
 *   LEADING `..` IS THE BUNDLER'S RELATIVE PREFIX, NOT AN ATTACK. A map in `dist/assets/` refers to
 *   `../../src/main.js` because that is genuinely where the file is — measured on this tool's own
 *   sample build, whose sources are exactly `["../../src/live.js", "../../src/main.js"]`. Those
 *   segments are DROPPED: re-rooting the path at the project root is precisely what they mean. More
 *   than four of them is climbing above any plausible project root, so that is rejected.
 *
 *   `..` AFTER A REAL SEGMENT IS A TRAVERSAL. `src/../../../.ssh/id_rsa` is rejected outright rather
 *   than popped, because popping turns a traversal into a plausible-looking path and hides that the
 *   input was hostile. A rejected entry becomes a visible line in the report ("skipped 3 entries
 *   with unsafe paths"), which is itself a finding.
 */

const WINDOWS_RESERVED = /^(con|prn|aux|nul|com[0-9¹²³]|lpt[0-9¹²³])(\..*)?$/i;
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;
const MAX_SEGMENTS = 64;
const MAX_SEGMENT_LENGTH = 255;
const MAX_LEADING_PARENT = 4;

export type PathResult = { ok: true; path: string } | { ok: false; reason: string };

/**
 * `sourceRoot` is STRING-PREPENDED, not URL-joined. Measured: `"https://cdn.example.com/src" +
 * "../secret.js"` gives `https://cdn.example.com/src../secret.js`, which is materially different
 * from what `new URL()` would produce. Bundlers do the string concatenation, so we must too — and
 * `sourceRoot` can itself carry `../`, which is why this runs BEFORE sanitisation.
 */
export function applySourceRoot(sourceRoot: string, source: string): string {
  if (!sourceRoot) return source;
  return sourceRoot.endsWith('/') ? sourceRoot + source : `${sourceRoot}/${source}`;
}

/**
 * Fold a sourcemap source or zip entry name into a safe, legible relative path.
 *
 * A scheme and authority become a legible top-level directory rather than being stripped, because
 * `webpack://my-app/./src/api.js` and `src/api.js` are genuinely different files and collapsing them
 * loses information the user needs.
 */
export function safeEntryPath(raw: string): PathResult {
  if (typeof raw !== 'string' || raw.length === 0) return { ok: false, reason: 'empty path' };
  if (CONTROL_CHARS.test(raw)) return { ok: false, reason: 'contains a control character or NUL' };

  let s = raw.replace(/\\/g, '/');

  // scheme://authority/rest  ->  scheme/authority/rest
  const scheme = /^([a-z][a-z0-9+.-]*):\/\/([^/]*)\/?(.*)$/i.exec(s);
  if (scheme) {
    const [, proto, authority, rest] = scheme;
    s = [proto!, authority || '_', rest ?? ''].filter(Boolean).join('/');
  } else {
    // `file:/path` and other single-slash schemes.
    const bare = /^([a-z][a-z0-9+.-]*):(.*)$/i.exec(s);
    if (bare && bare[1]!.length > 1) s = `${bare[1]}/${bare[2]}`;
  }

  // Windows drive letter.
  s = s.replace(/^\/?([A-Za-z]):\//, '$1/');

  const segments: string[] = [];
  let leadingParents = 0;
  for (const seg of s.split('/')) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      // Leading `..` is the bundler saying "up out of dist/assets"; drop it and re-root the path.
      // A `..` once real segments exist is a genuine traversal and is refused, never popped.
      if (segments.length > 0) return { ok: false, reason: 'contains a parent-directory traversal (..)' };
      if (++leadingParents > MAX_LEADING_PARENT) {
        return { ok: false, reason: 'climbs more than four levels above the build, which no source path legitimately does' };
      }
      continue;
    }
    if (seg.length > MAX_SEGMENT_LENGTH) return { ok: false, reason: 'path segment longer than 255 characters' };
    if (WINDOWS_RESERVED.test(seg)) return { ok: false, reason: `"${seg}" is a reserved name on Windows` };
    if (/[ .]$/.test(seg)) return { ok: false, reason: 'path segment ends with a space or a dot' };
    segments.push(seg);
  }
  if (segments.length === 0) return { ok: false, reason: 'path resolves to nothing' };
  if (segments.length > MAX_SEGMENTS) return { ok: false, reason: 'more than 64 path segments' };

  return { ok: true, path: segments.join('/') };
}

/**
 * De-duplicate across the WHOLE recovered tree, not per map. A multi-chunk build has N maps that all
 * contain `webpack://app/./src/index.js`; identical contents collapse to one file, differing
 * contents get a `~2` suffix so nothing is silently lost.
 */
export function dedupeTree(files: { path: string; content: string }[]): { path: string; content: string }[] {
  const byPath = new Map<string, string[]>();
  const out: { path: string; content: string }[] = [];

  for (const f of files) {
    const seen = byPath.get(f.path);
    if (!seen) {
      byPath.set(f.path, [f.content]);
      out.push(f);
      continue;
    }
    if (seen.includes(f.content)) continue; // byte-identical duplicate
    seen.push(f.content);
    const dot = f.path.lastIndexOf('.');
    const suffix = `~${seen.length}`;
    const path = dot > f.path.lastIndexOf('/') ? `${f.path.slice(0, dot)}${suffix}${f.path.slice(dot)}` : `${f.path}${suffix}`;
    out.push({ path, content: f.content });
  }
  return out;
}
